import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, RefreshCw, CircleCheck, X, FileDown, Pencil, Search, AlertTriangle, Send, Pause, Play } from "lucide-react";
import api, { formatErr, API } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";
import { deNumber } from "../lib/numberFormat";

// Feb 2026 · 17 Sep 2026 · Rebuilt für reinen Upload-Flow.
// User-Directive: „Wir haben doch keinen sevdesk-Tarif mit API-Zugang."
// Entfernt: Sync-Toggle, Match-Suggestions, „Jetzt synchronisieren".
// Neu: Multi-File-Upload mit Kundennummer-Erkennung im Dateinamen.

const STATUS_OPTIONS = [
  { v: "open",           label: "Offen" },
  { v: "partially_paid", label: "Teilbezahlt" },
  { v: "paid",           label: "Bezahlt" },
  { v: "cancelled",      label: "Storniert" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.v, s.label]));
const STATUS_BG = {
  open: BRAND_COLORS.gold, partially_paid: BRAND_COLORS.gold,
  paid: STATUS_COLORS.green, cancelled: STATUS_COLORS.red,
};

function StatusBadge({ status, onChange, testid }) {
  if (!onChange) {
    return (
      <span
        data-testid={testid}
        className="inline-flex px-2 py-0.5 text-[10.5px] tracking-widest uppercase"
        style={{ background: STATUS_BG[status] || "#e5e7eb", color: status === "paid" || status === "cancelled" ? "#fff" : BRAND_COLORS.navy }}
      >
        {STATUS_LABEL[status] || status}
      </span>
    );
  }
  return (
    <select
      value={status || "open"}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1 text-[12px]"
    >
      {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
    </select>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return deDateMedium(iso);
}
function fmtMoney(n, cur = "EUR") {
  return `${deNumber(n || 0, 2)} ${cur}`;
}

// ------- Multi-Upload-Zeile ---------------------------------------------
function UploadRow({ row, customers, onChange, onRemove, onUpload }) {
  const isMismatch = row.preview?.status === "mismatch";
  const isUnknown = row.preview?.status === "unknown" && row.preview?.warning;
  const hasDuplicate = (row.duplicates?.length || 0) > 0;
  const canUpload =
    row.state !== "uploading"
    && row.state !== "uploaded"
    && (!isMismatch || row.mismatch_confirmed)
    && (!hasDuplicate || row.duplicate_confirmed);
  return (
    <div className="bg-white border border-navy/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`upload-row-${row.key}`}>
      <div className="md:col-span-3 min-w-0">
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Datei</div>
        <div className="text-[13px] text-navy truncate" title={row.file.name}>{row.file.name}</div>
        <div className="text-[11px] text-navy-dark/50 mt-0.5">{(row.file.size / 1024).toFixed(0)} KB</div>
        {row.matched_token && (
          <div className="text-[10.5px] text-emerald-700 mt-1">Erkannt: {row.matched_token}</div>
        )}
      </div>
      <div className="md:col-span-3">
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Kunde</div>
        <select
          value={row.customer_id || ""}
          onChange={(e) => onChange({ customer_id: e.target.value || null })}
          data-testid={`upload-row-customer-${row.key}`}
          className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]"
        >
          <option value="">— (später zuordnen)</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {(c.name || c.email)}{c.sevdesk_customer_number ? ` · Nr. ${c.sevdesk_customer_number}` : ""}
            </option>
          ))}
        </select>
        {/* Feb 2026 · 27 Feb 2026 · User-Directive: „Sobald ein Kunde
            ausgewählt wird, soll die hinterlegte Kundennummer aus dem
            Kundenprofil automatisch in das Kundennummer-Feld der Rechnung
            eingetragen werden." Read-only Chip, damit klar ist, was auf
            dieser Rechnung landet. Nicht editierbar — die Nummer wird
            serverseitig immer aus dem Kundenprofil gezogen. */}
        {row.customer_id && (() => {
          const cust = customers.find((c) => c.id === row.customer_id);
          const num = cust?.sevdesk_customer_number || "";
          return (
            <div
              className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] tracking-wide"
              data-testid={`upload-row-customer-number-${row.key}`}
            >
              <span className="uppercase text-navy-dark/50">Kd-Nr.</span>
              {num ? (
                <span
                  className="font-mono font-semibold text-navy px-1.5 py-0.5 border border-navy/20"
                  style={{ background: withAlpha(BRAND_COLORS.gold, 0.10) }}
                  data-testid={`upload-row-customer-number-value-${row.key}`}
                >
                  {num}
                </span>
              ) : (
                <span className="italic text-red-700" data-testid={`upload-row-customer-number-missing-${row.key}`}>
                  keine im Profil hinterlegt
                </span>
              )}
            </div>
          );
        })()}
      </div>
      <div className="md:col-span-2">
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Nummer *</div>
        <input
          value={row.invoice_number}
          onChange={(e) => onChange({ invoice_number: e.target.value })}
          data-testid={`upload-row-number-${row.key}`}
          className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]"
          placeholder="RE-2026-001"
        />
      </div>
      <div className="md:col-span-2">
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Datum *</div>
        <input
          type="date"
          value={row.invoice_date}
          onChange={(e) => onChange({ invoice_date: e.target.value })}
          data-testid={`upload-row-date-${row.key}`}
          className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]"
        />
      </div>
      <div className="md:col-span-1">
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Betrag *</div>
        <input
          value={row.sum_gross}
          onChange={(e) => onChange({ sum_gross: e.target.value })}
          data-testid={`upload-row-gross-${row.key}`}
          className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] tabular-nums text-right"
          placeholder="0,00"
        />
      </div>
      <div className="md:col-span-1">
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Status</div>
        <select
          value={row.status}
          onChange={(e) => onChange({ status: e.target.value })}
          data-testid={`upload-row-status-${row.key}`}
          className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]"
        >
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      {/* Feb 2026 · 27 Feb 2026 · Kundennummer-Mismatch-Warnung (User-Directive).
          Blockiert den Upload, bis der Admin explizit „Trotzdem hochladen"
          bestätigt. Serverseitig läuft der gleiche Guard (409 bei mismatch
          ohne Confirm). */}
      {(isMismatch || isUnknown) && (
        <div
          className={`md:col-span-12 p-3 border-l-4 flex items-start gap-2 ${isMismatch ? "" : ""}`}
          style={{
            background: isMismatch ? withAlpha(STATUS_COLORS.red, 0.08) : withAlpha(BRAND_COLORS.gold, 0.10),
            borderLeftColor: isMismatch ? STATUS_COLORS.red : BRAND_COLORS.gold,
          }}
          data-testid={`upload-row-mismatch-${row.key}`}
        >
          <AlertTriangle
            className="w-4 h-4 shrink-0 mt-0.5"
            strokeWidth={1.8}
            style={{ color: isMismatch ? STATUS_COLORS.red : BRAND_COLORS.goldDark }}
          />
          <div className="min-w-0">
            <div
              className="text-[11px] tracking-widest uppercase font-bold"
              style={{ color: isMismatch ? STATUS_COLORS.red : BRAND_COLORS.navy }}
            >
              {isMismatch ? "Kundennummer stimmt nicht überein" : "Kundennummer nicht gefunden"}
            </div>
            <div className="text-[12.5px] text-navy-dark/85 mt-1 leading-snug">
              {row.preview?.warning}
              {row.preview?.expected && (
                <> · Erwartet: <b>{row.preview.expected}</b></>
              )}
            </div>
            {isMismatch && (
              <label
                className="inline-flex items-center gap-2 mt-2 text-[12px] text-navy select-none cursor-pointer"
                data-testid={`upload-row-mismatch-confirm-${row.key}`}
              >
                <input
                  type="checkbox"
                  checked={!!row.mismatch_confirmed}
                  onChange={(e) => onChange({ mismatch_confirmed: e.target.checked })}
                  className="accent-navy w-4 h-4"
                  data-testid={`upload-row-mismatch-confirm-input-${row.key}`}
                />
                Trotzdem hochladen (ich habe die Nummer manuell geprüft)
              </label>
            )}
          </div>
        </div>
      )}

      {/* Match-Bestätigung (dezent, grün) */}
      {row.preview?.status === "match" && (
        <div className="md:col-span-12 flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid={`upload-row-match-${row.key}`}>
          <CircleCheck className="w-3.5 h-3.5" /> Kundennummer im PDF gefunden ({row.preview.expected})
        </div>
      )}

      {/* Feb 2026 · 27 Feb 2026 · Duplikat-Warnung.
          Blockiert den Upload, bis der Admin „Duplikat trotzdem speichern"
          bestätigt. Serverseitig läuft der gleiche Guard (409 duplicate_invoice). */}
      {(row.duplicates?.length || 0) > 0 && (
        <div
          className="md:col-span-12 p-3 border-l-4 flex items-start gap-2"
          style={{
            background: withAlpha(STATUS_COLORS.red, 0.08),
            borderLeftColor: STATUS_COLORS.red,
          }}
          data-testid={`upload-row-duplicate-${row.key}`}
        >
          <AlertTriangle
            className="w-4 h-4 shrink-0 mt-0.5"
            strokeWidth={1.8}
            style={{ color: STATUS_COLORS.red }}
          />
          <div className="min-w-0">
            <div
              className="text-[11px] tracking-widest uppercase font-bold"
              style={{ color: STATUS_COLORS.red }}
            >
              {row.duplicates.length === 1 ? "Duplikat gefunden" : `${row.duplicates.length} Duplikate gefunden`}
            </div>
            <ul className="text-[12.5px] text-navy-dark/85 mt-1 leading-snug space-y-0.5 list-disc pl-4">
              {row.duplicates.slice(0, 5).map((d) => (
                <li key={d.id} data-testid={`upload-row-duplicate-item-${row.key}-${d.id}`}>
                  <span className="font-mono">{d.invoice_number}</span>
                  {d.invoice_date && <> · {deDateMedium(d.invoice_date)}</>}
                  {d.pdf_filename && <> · <span className="text-navy-dark/60">{d.pdf_filename}</span></>}
                </li>
              ))}
              {row.duplicates.length > 5 && (
                <li className="text-navy-dark/55">… und {row.duplicates.length - 5} weitere</li>
              )}
            </ul>
            <label
              className="inline-flex items-center gap-2 mt-2 text-[12px] text-navy select-none cursor-pointer"
              data-testid={`upload-row-duplicate-confirm-${row.key}`}
            >
              <input
                type="checkbox"
                checked={!!row.duplicate_confirmed}
                onChange={(e) => onChange({ duplicate_confirmed: e.target.checked })}
                className="accent-navy w-4 h-4"
                data-testid={`upload-row-duplicate-confirm-input-${row.key}`}
              />
              Duplikat trotzdem speichern
            </label>
          </div>
        </div>
      )}

      {/* Feb 2026 · Rechnung-Serie: „Alle N Tage duplizieren". Markiert diese
          Rechnung als wiederkehrende Vorlage — der tägliche Sweep legt daraus
          Entwürfe an, die der Admin im Tab „Entwürfe" freigibt. */}
      <div className="md:col-span-12 flex flex-wrap items-center gap-2 pt-2 border-t border-navy/5 mt-1" data-testid={`upload-row-series-${row.key}`}>
        <label className="inline-flex items-center gap-2 text-[12px] text-navy select-none cursor-pointer">
          <input
            type="checkbox"
            checked={!!row.is_series}
            onChange={(e) => onChange({ is_series: e.target.checked })}
            className="accent-navy w-4 h-4"
            data-testid={`upload-row-series-toggle-${row.key}`}
          />
          Als Serie: automatisch alle
        </label>
        <input
          type="number"
          min="1"
          max="366"
          value={row.series_interval_days}
          onChange={(e) => onChange({ series_interval_days: e.target.value })}
          disabled={!row.is_series}
          data-testid={`upload-row-series-interval-${row.key}`}
          className="w-16 bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1 text-[13px] text-right disabled:opacity-40"
        />
        <span className="text-[12px] text-navy-dark/70">Tage als Entwurf duplizieren</span>
      </div>

      <div className="md:col-span-12 flex items-center justify-end gap-2 pt-1">
        {row.state === "uploaded"
          ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CircleCheck className="w-3.5 h-3.5" /> Hochgeladen</span>
          : row.state === "error"
          ? <span className="text-[11px] text-red-700">{row.error || "Fehler"}</span>
          : null}
        <button
          type="button"
          onClick={onRemove}
          data-testid={`upload-row-remove-${row.key}`}
          className="text-[11px] uppercase tracking-widest text-navy-dark/60 hover:text-red-700 px-2 py-1"
        >
          <X className="w-3.5 h-3.5 inline" />
        </button>
        <button
          type="button"
          onClick={onUpload}
          disabled={!canUpload}
          data-testid={`upload-row-upload-${row.key}`}
          className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5 disabled:opacity-40"
          style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
        >
          {row.state === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Hochladen
        </button>
      </div>
    </div>
  );
}

// ------- Serien-Entwurf-Zeile -------------------------------------------
function DraftRow({ draft, customers, onFinalize, onDiscard, onDownload }) {
  const [number, setNumber] = useState(draft.invoice_number || "");
  const [d, setD] = useState((draft.invoice_date || "").slice(0, 10));
  const [gross, setGross] = useState(draft.sum_gross ?? "");
  const [status, setStatus] = useState(draft.status || "open");
  const cust = customers.find((c) => c.id === draft.customer_id);
  return (
    <div className="bg-white border border-navy/10 p-4 space-y-3" data-testid={`draft-row-${draft.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9.5px] tracking-widest uppercase px-2 py-0.5" style={{ background: withAlpha(BRAND_COLORS.gold, 0.18), color: BRAND_COLORS.goldDark }}>Serien-Entwurf</span>
        {cust
          ? <span className="text-[12px] text-navy-dark/70">Kunde: <b>{cust.name || cust.email}</b></span>
          : <span className="text-[12px] text-red-700">Kein Kunde zugeordnet</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4">
          <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Nummer</div>
          <input value={number} onChange={(e) => setNumber(e.target.value)} data-testid={`draft-number-${draft.id}`}
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]" />
        </div>
        <div className="md:col-span-3">
          <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Datum</div>
          <input type="date" value={d} onChange={(e) => setD(e.target.value)} data-testid={`draft-date-${draft.id}`}
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]" />
        </div>
        <div className="md:col-span-2">
          <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Betrag</div>
          <input value={gross} onChange={(e) => setGross(e.target.value)} data-testid={`draft-gross-${draft.id}`}
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] tabular-nums text-right" />
        </div>
        <div className="md:col-span-3">
          <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-1">Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`draft-status-${draft.id}`}
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px]">
            {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        {draft.pdf_filename && (
          <button type="button" onClick={() => onDownload(draft)} data-testid={`draft-pdf-${draft.id}`}
            className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase px-3 py-1.5 border border-navy/25 hover:border-gold hover:bg-gold/5">
            <FileDown className="w-3.5 h-3.5" /> PDF
          </button>
        )}
        <button type="button" onClick={() => onDiscard(draft.id)} data-testid={`draft-discard-${draft.id}`}
          className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50">
          <Trash2 className="w-3.5 h-3.5" /> Verwerfen
        </button>
        <button type="button" data-testid={`draft-finalize-${draft.id}`}
          onClick={() => {
            const g = Number(String(gross).replace(",", "."));
            onFinalize(draft.id, {
              invoice_number: number, invoice_date: d, status,
              sum_gross: Number.isFinite(g) ? g : undefined,
            });
          }}
          className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-3 py-1.5"
          style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
          <Send className="w-3.5 h-3.5" /> Freigeben &amp; senden
        </button>
      </div>
    </div>
  );
}

// ------- Serien-Vorlage-Zeile -------------------------------------------
function SeriesRow({ tmpl, customers, onToggle, onInterval, onStop }) {
  const [iv, setIv] = useState(tmpl.series_interval_days || 30);
  const cust = customers.find((c) => c.id === tmpl.customer_id);
  const active = tmpl.series_active !== false;
  return (
    <div className="bg-white border border-navy/10 p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6" data-testid={`series-row-${tmpl.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-lg text-navy">{tmpl.invoice_number || "Rechnung"}</span>
          <span className="text-[9.5px] tracking-widest uppercase px-2 py-0.5"
            style={{ background: active ? withAlpha(STATUS_COLORS.green, 0.15) : "#e5e7eb", color: active ? STATUS_COLORS.green : BRAND_COLORS.navy }}
            data-testid={`series-active-${tmpl.id}`}>
            {active ? "Aktiv" : "Pausiert"}
          </span>
        </div>
        <div className="text-[12px] text-navy-dark/60 mt-1">
          {fmtMoney(tmpl.sum_gross, tmpl.currency)}
          {cust && <> · Kunde: <b>{cust.name || cust.email}</b></>}
          {tmpl.series_next_spawn_at && <> · Nächster Entwurf: <b data-testid={`series-next-${tmpl.id}`}>{fmtDate(tmpl.series_next_spawn_at)}</b></>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-1 text-[11px] text-navy-dark/70">
          alle
          <input type="number" min="1" max="366" value={iv} onChange={(e) => setIv(e.target.value)}
            onBlur={() => Number(iv) !== tmpl.series_interval_days && onInterval(tmpl.id, iv)}
            data-testid={`series-interval-${tmpl.id}`}
            className="w-16 bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1 text-[13px] text-right" />
          Tage
        </label>
        <button type="button" onClick={() => onToggle(tmpl.id, !active)} data-testid={`series-toggle-${tmpl.id}`}
          className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase px-3 py-1.5 border border-navy/25 hover:border-gold hover:bg-gold/5">
          {active ? <><Pause className="w-3.5 h-3.5" /> Pausieren</> : <><Play className="w-3.5 h-3.5" /> Fortsetzen</>}
        </button>
        <button type="button" onClick={() => onStop(tmpl.id)} data-testid={`series-stop-${tmpl.id}`}
          className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50">
          <X className="w-3.5 h-3.5" /> Beenden
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
export default function TeamSevdesk() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [rows, setRows] = useState([]); // upload draft rows
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [series, setSeries] = useState([]);

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/sevdesk/settings"); setStats(data); } catch (e) { console.debug("sevdesk settings load failed:", e); }
  };
  const loadCustomers = async (q = "") => {
    try { const { data } = await api.get(`/admin/sevdesk/customer-search?q=${encodeURIComponent(q)}`); setCustomers(data || []); } catch (e) { console.debug("sevdesk customer-search failed:", e); }
  };
  const loadInvoices = async () => {
    try { const { data } = await api.get("/admin/sevdesk/invoices"); setInvoices(data || []); } catch (e) { console.debug("sevdesk invoices load failed:", e); }
  };
  const loadDrafts = async () => {
    try { const { data } = await api.get("/admin/sevdesk/drafts"); setDrafts(data || []); } catch (e) { console.debug("sevdesk drafts load failed:", e); }
  };
  const loadSeries = async () => {
    try { const { data } = await api.get("/admin/sevdesk/series"); setSeries(data || []); } catch (e) { console.debug("sevdesk series load failed:", e); }
  };
  useEffect(() => { loadStats(); loadCustomers(); loadInvoices(); loadDrafts(); loadSeries(); }, []);

  // --------- Multi-Upload -----------------------------------------------
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    if (files.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const drafts = await Promise.all(files.map(async (file) => {
      let matched_token = null;
      let customer_id = null;
      try {
        const { data } = await api.post("/admin/sevdesk/suggest-customer-for-filename", { filename: file.name });
        if (data?.customer) { customer_id = data.customer.id; matched_token = data.matched_token; }
      } catch (e) { console.debug("suggest-customer-for-filename failed:", e); }
      return {
        key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
        file, customer_id, matched_token,
        invoice_number: file.name.replace(/\.pdf$/i, ""),
        invoice_date: today,
        sum_gross: "",
        status: "open",
        state: "draft",
        preview: null,
        mismatch_confirmed: false,
        duplicates: [],
        duplicate_confirmed: false,
        is_series: false,
        series_interval_days: 30,
      };
    }));
    setRows((cur) => [...cur, ...drafts]);
    // Feb 2026 · 27 Feb 2026 · Async PDF-Kundennummer-Vergleich + Duplikat-
    // Check für jede Datei. Läuft asynchron, blockiert die UI nicht.
    for (const d of drafts) {
      if (d.customer_id) runPreview(d);
      runDuplicateCheck(d);
    }
  };
  const runPreview = async (row) => {
    if (!row.customer_id) {
      updateRow(row.key, { preview: null });
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", row.file);
      fd.append("customer_id", row.customer_id);
      const { data } = await api.post(
        "/admin/sevdesk/pdf-preview",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      updateRow(row.key, { preview: data, mismatch_confirmed: false });
    } catch (err) {
      updateRow(row.key, {
        preview: {
          status: "unknown",
          warning: formatErr(err?.response?.data?.detail) || "PDF-Vorschau fehlgeschlagen.",
          expected: null,
          candidates: [],
        },
      });
    }
  };
  const runDuplicateCheck = async (row) => {
    try {
      const { data } = await api.post("/admin/sevdesk/check-duplicate", {
        filename: row.file.name,
        invoice_number: row.invoice_number,
      });
      updateRow(row.key, {
        duplicates: data?.duplicates || [],
        duplicate_confirmed: false,
      });
    } catch (e) { console.debug("sevdesk duplicate-check failed:", e); }
  };
  const updateRow = (key, patch) => setRows((cur) => cur.map((r) => r.key === key ? { ...r, ...patch } : r));
  const removeRow = (key) => setRows((cur) => cur.filter((r) => r.key !== key));
  // Feb 2026 · 27 Feb 2026 · Bei Kundenwechsel im Dropdown erneut prüfen.
  // Bei Nummern-Änderung Duplikat-Check triggern.
  const changeRow = (key, patch) => {
    setRows((cur) => {
      const next = cur.map((r) => r.key === key ? { ...r, ...patch } : r);
      const updated = next.find((r) => r.key === key);
      if (!updated) return next;
      if ("customer_id" in patch) {
        Promise.resolve().then(() => {
          updateRow(key, { preview: null, mismatch_confirmed: false });
          if (updated.customer_id) runPreview(updated);
        });
      }
      if ("invoice_number" in patch) {
        Promise.resolve().then(() => {
          runDuplicateCheck(updated);
        });
      }
      return next;
    });
  };
  const uploadRow = async (row) => {
    if (!row.invoice_number || !row.invoice_date || !row.sum_gross) {
      toast.error("Nummer, Datum und Betrag sind erforderlich.");
      return;
    }
    if ((row.duplicates?.length || 0) > 0 && !row.duplicate_confirmed) {
      toast.error('Duplikat gefunden. Bitte „Duplikat trotzdem speichern" bestätigen.');
      return;
    }
    if (row.preview?.status === "mismatch" && !row.mismatch_confirmed) {
      toast.error('Kundennummer im PDF passt nicht. Bitte „Trotzdem hochladen" bestätigen.');
      return;
    }
    updateRow(row.key, { state: "uploading", error: null });
    try {
      const fd = new FormData();
      fd.append("file", row.file);
      if (row.customer_id) fd.append("customer_id", row.customer_id);
      fd.append("invoice_number", row.invoice_number);
      fd.append("invoice_date", row.invoice_date);
      fd.append("sum_gross", row.sum_gross);
      fd.append("currency", "EUR");
      fd.append("status", row.status);
      fd.append("mismatch_confirmed", row.mismatch_confirmed ? "true" : "false");
      fd.append("duplicate_confirmed", row.duplicate_confirmed ? "true" : "false");
      fd.append("is_series", row.is_series ? "true" : "false");
      fd.append("series_interval_days", String(row.series_interval_days || 30));
      await api.post("/admin/sevdesk/invoices", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateRow(row.key, { state: "uploaded" });
      loadStats(); loadInvoices(); loadSeries(); loadDrafts();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) ? detail.message : (formatErr(detail) || err.message);
      updateRow(row.key, { state: "error", error: msg });
      // Bei serverseitig erkanntem Duplikat die Liste ins UI übernehmen,
      // damit der Admin die betroffenen Rechnungen sieht.
      if (err?.response?.status === 409 && detail?.code === "duplicate_invoice") {
        updateRow(row.key, { duplicates: detail.duplicates || [], duplicate_confirmed: false });
        toast.error("Duplikat erkannt — siehe Warnung an der Zeile.");
      } else if (err?.response?.status === 409) {
        toast.error("Kundennummer-Mismatch — bitte Warnung bestätigen.");
      }
    }
  };
  const uploadAll = async () => {
    setBusy(true);
    for (const r of rows) { if (r.state !== "uploaded") { await uploadRow(r); } }
    setBusy(false);
    toast.success("Alle Rechnungen verarbeitet.");
  };
  const clearUploaded = () => setRows((cur) => cur.filter((r) => r.state !== "uploaded"));

  // --------- Status Edit -----------------------------------------------
  const patchStatus = async (id, status) => {
    try { await api.patch(`/admin/sevdesk/invoices/${id}`, { status }); loadInvoices(); loadStats(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const download = async (inv) => {
    try {
      const url = `${API}/customer/sevdesk-invoices/${inv.id}/pdf`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("PDF-Download fehlgeschlagen");
      const blob = await r.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = inv.pdf_filename || `rechnung-${inv.invoice_number || inv.id}.pdf`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (err) { toast.error(err.message); }
  };
  const assign = async (invoiceId, customerId) => {
    if (!customerId) return;
    try { await api.post(`/admin/sevdesk/invoices/${invoiceId}/assign`, { customer_id: customerId });
      toast.success("Zugeordnet."); loadInvoices(); loadStats();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };

  // --------- CSV + Test -------------------------------------------------
  const uploadCsv = async (e) => {
    const f = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/sevdesk/csv-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.matched} Zuordnungen importiert${data.not_found_emails?.length ? ` · ${data.not_found_emails.length} unbekannte E-Mails` : ""}`);
      loadInvoices(); loadStats(); loadCustomers();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const seedTest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/sevdesk/test-data/seed"); toast.success(`${data.created} Testrechnungen erzeugt.`); loadInvoices(); loadStats(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
    setBusy(false);
  };
  const deleteTest = async () => {
    if (!window.confirm("Alle Testrechnungen wirklich löschen?")) return;
    setBusy(true);
    try { const { data } = await api.delete("/admin/sevdesk/test-data"); toast.success(`${data.deleted} Testrechnungen gelöscht.`); loadInvoices(); loadStats(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
    setBusy(false);
  };

  // --------- Rechnung-Serien -------------------------------------------
  const finalizeDraft = async (id, patch) => {
    try {
      await api.post(`/admin/sevdesk/drafts/${id}/finalize`, patch);
      toast.success("Entwurf freigegeben — der Kunde sieht die Rechnung jetzt.");
      loadDrafts(); loadInvoices(); loadStats();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const discardDraft = async (id) => {
    if (!window.confirm("Entwurf verwerfen?")) return;
    try { await api.delete(`/admin/sevdesk/drafts/${id}`); toast.success("Entwurf verworfen."); loadDrafts(); loadStats(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const toggleSeries = async (id, active) => {
    try { await api.patch(`/admin/sevdesk/series/${id}`, { active }); loadSeries(); loadStats(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const setSeriesInterval = async (id, interval_days) => {
    try { await api.patch(`/admin/sevdesk/series/${id}`, { interval_days: Number(interval_days) }); loadSeries(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const stopSeries = async (id) => {
    if (!window.confirm("Serie beenden? Die ursprüngliche Rechnung bleibt erhalten.")) return;
    try { await api.delete(`/admin/sevdesk/series/${id}`); toast.success("Serie beendet."); loadSeries(); loadStats(); }
    catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
  };
  const spawnSeriesNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sevdesk/series/spawn-now");
      toast.success(`${data.spawned || 0} Entwurf/Entwürfe erzeugt.`);
      loadDrafts(); loadSeries(); loadStats();
      if ((data.spawned || 0) > 0) setTab("drafts");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail) || err.message); }
    setBusy(false);
  };

  const filteredInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((r) => {
      const num = (r.invoice_number || "").toLowerCase();
      const cust = customers.find((c) => c.id === r.customer_id);
      const name = (cust?.name || cust?.email || "").toLowerCase();
      return num.includes(q) || name.includes(q);
    });
  }, [invoices, customers, search]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 pb-24" data-testid="team-sevdesk-page">
      <h1 className="font-display text-3xl md:text-4xl text-navy mb-1">Rechnungs-Upload</h1>
      <p className="text-navy-dark/70 text-[13.5px] mb-6 leading-relaxed">
        Rechnungen als PDF hochladen und Kunden zuordnen. Kunden sehen ihre eigenen Rechnungen im Bereich „Meine Rechnungen".
      </p>

      {stats && (
        <div className="bg-white border border-navy/10 p-4 mb-6 grid grid-cols-2 md:grid-cols-5 gap-6">
          <div>
            <div className="label-caps text-navy-dark/50">Gesamt</div>
            <div className="text-2xl text-navy tabular-nums" data-testid="team-sevdesk-total">{stats.total_invoices || 0}</div>
          </div>
          <div>
            <div className="label-caps text-navy-dark/50">Nicht zugeordnet</div>
            <div className="text-2xl tabular-nums" style={{ color: (stats.unassigned_invoices || 0) > 0 ? STATUS_COLORS.red : BRAND_COLORS.navy }} data-testid="team-sevdesk-unassigned">{stats.unassigned_invoices || 0}</div>
          </div>
          <div>
            <div className="label-caps text-navy-dark/50">Entwürfe</div>
            <div className="text-2xl tabular-nums" style={{ color: (stats.draft_invoices || 0) > 0 ? BRAND_COLORS.goldDark : BRAND_COLORS.navy }} data-testid="team-sevdesk-draft-count">{stats.draft_invoices || 0}</div>
          </div>
          <div>
            <div className="label-caps text-navy-dark/50">Serien</div>
            <div className="text-2xl text-navy tabular-nums" data-testid="team-sevdesk-series-count">{stats.active_series || 0}</div>
          </div>
          <div>
            <div className="label-caps text-navy-dark/50">Testdaten</div>
            <div className="text-2xl text-navy tabular-nums" data-testid="team-sevdesk-test-count">{stats.test_invoices || 0}</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-navy/10 mb-6 overflow-x-auto">
        {[
          ["upload", "Hochladen"],
          ["list", `Alle Rechnungen (${stats?.total_invoices || 0})`],
          ["unassigned", `Nicht zugeordnet (${stats?.unassigned_invoices || 0})`],
          ["drafts", `Entwürfe (${stats?.draft_invoices || 0})`],
          ["series", `Serien (${stats?.active_series || 0})`],
          ["csv", "CSV-Import"],
          ["test", "Testdaten"],
        ].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            data-testid={`team-sevdesk-tab-${k}`} data-active={tab === k}
            className={`px-4 py-2.5 text-[11px] tracking-widest uppercase transition-colors ${
              tab === k ? "text-navy border-b-2 border-gold" : "text-navy-dark/50 hover:text-navy"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "upload" && (
        <section className="space-y-4" data-testid="team-sevdesk-upload-section">
          <div className="bg-white border border-navy/10 p-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-display text-lg text-navy">Neue Rechnungen wählen</div>
              <p className="text-[12.5px] text-navy-dark/70 mt-1 max-w-xl leading-relaxed">
                Wählen Sie ein oder mehrere PDFs. Enthält der Dateiname eine hinterlegte Kundennummer, wird der passende Kunde automatisch vorgeschlagen — Sie bestätigen jede Zuordnung.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-widest uppercase cursor-pointer" style={{ background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory }}>
              <Upload className="w-3.5 h-3.5" strokeWidth={2} /> PDF(s) wählen
              <input type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={onFiles} data-testid="team-sevdesk-file-input" />
            </label>
          </div>
          {rows.length > 0 && (
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={clearUploaded} className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy px-3 py-1.5" data-testid="team-sevdesk-clear-uploaded">
                Fertige entfernen
              </button>
              <button type="button" onClick={uploadAll} disabled={busy} data-testid="team-sevdesk-upload-all-btn"
                className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-4 py-2 disabled:opacity-40"
                style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Alle hochladen
              </button>
            </div>
          )}
          {rows.map((r) => (
            <UploadRow key={r.key} row={r} customers={customers}
              onChange={(patch) => changeRow(r.key, patch)}
              onRemove={() => removeRow(r.key)}
              onUpload={() => uploadRow(r)} />
          ))}
        </section>
      )}

      {(tab === "list" || tab === "unassigned") && (
        <section className="space-y-3" data-testid={`team-sevdesk-${tab}-section`}>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-dark/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche nach Nummer oder Kundenname…"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none pl-9 pr-3 py-2 text-[13px]" />
          </div>
          {filteredInvoices
            .filter((r) => tab === "list" || !r.customer_id)
            .map((r) => {
              const cust = customers.find((c) => c.id === r.customer_id);
              return (
                <div key={r.id} data-testid={`invoice-row-${r.id}`} className="bg-white border border-navy/10 p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg text-navy">{r.invoice_number || "Rechnung"}</span>
                      <StatusBadge status={r.status} onChange={(s) => patchStatus(r.id, s)} testid={`invoice-status-${r.id}`} />
                      {r.is_test && <span className="text-[9.5px] tracking-widest uppercase px-2 py-0.5" style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}>Testdaten</span>}
                      {r.is_ocr_extracted && (
                        <span
                          data-testid={`invoice-ocr-badge-${r.id}`}
                          title="Text wurde per OCR aus einem Scan/Bild-PDF extrahiert. Bitte gegenprüfen."
                          className="text-[9.5px] tracking-widest uppercase px-2 py-0.5"
                          style={{ background: withAlpha(STATUS_COLORS.red, 0.10), color: STATUS_COLORS.red }}
                        >
                          OCR
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-navy-dark/60 mt-1">
                      {fmtDate(r.invoice_date)} · {fmtMoney(r.sum_gross, r.currency)}
                      {cust && <> · Kunde: <b>{cust.name || cust.email}</b></>}
                      {r.sevdesk_customer_number && (
                        <span data-testid={`invoice-cust-num-${r.id}`} className="ml-2 text-navy-dark/80">
                          · Kd-Nr. <b>{r.sevdesk_customer_number}</b>
                        </span>
                      )}
                    </div>
                  </div>
                  {!r.customer_id && (
                    <select onChange={(e) => assign(r.id, e.target.value)} defaultValue="" data-testid={`invoice-assign-${r.id}`}
                      className="bg-white border border-navy/25 focus:border-gold focus:outline-none px-2 py-1.5 text-[12.5px] min-w-[200px]">
                      <option value="">Kunde zuordnen…</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
                    </select>
                  )}
                  {r.pdf_filename && (
                    <button type="button" onClick={() => download(r)} data-testid={`invoice-pdf-btn-${r.id}`}
                      className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase px-3 py-1.5 border border-navy/25 hover:border-gold hover:bg-gold/5">
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </button>
                  )}
                </div>
              );
            })}
          {filteredInvoices.filter((r) => tab === "list" || !r.customer_id).length === 0 && (
            <div className="bg-white border border-navy/10 p-6 text-navy-dark/70 italic text-sm">Keine Rechnungen gefunden.</div>
          )}
        </section>
      )}

      {tab === "drafts" && (
        <section className="space-y-3" data-testid="team-sevdesk-drafts-section">
          <p className="text-[12.5px] text-navy-dark/70 leading-relaxed">
            Automatisch erzeugte Serien-Entwürfe. Prüfen Sie Nummer, Datum und Betrag und geben Sie die Rechnung frei — erst dann sieht der Kunde sie.
          </p>
          {drafts.length === 0 && (
            <div className="bg-white border border-navy/10 p-6 text-navy-dark/70 italic text-sm">Keine Entwürfe vorhanden.</div>
          )}
          {drafts.map((d) => (
            <DraftRow key={d.id} draft={d} customers={customers}
              onFinalize={finalizeDraft} onDiscard={discardDraft} onDownload={download} />
          ))}
        </section>
      )}

      {tab === "series" && (
        <section className="space-y-3" data-testid="team-sevdesk-series-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[12.5px] text-navy-dark/70 leading-relaxed max-w-xl">
              Wiederkehrende Rechnungen. Am fälligen Tag wird automatisch ein Entwurf erzeugt, den Sie im Tab „Entwürfe" freigeben.
            </p>
            <button type="button" onClick={spawnSeriesNow} disabled={busy} data-testid="team-sevdesk-spawn-now-btn"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-4 py-2 disabled:opacity-40"
              style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Jetzt fällige erzeugen
            </button>
          </div>
          {series.length === 0 && (
            <div className="bg-white border border-navy/10 p-6 text-navy-dark/70 italic text-sm">Keine Serien angelegt. Aktivieren Sie beim Upload „Als Serie".</div>
          )}
          {series.map((t) => (
            <SeriesRow key={t.id} tmpl={t} customers={customers}
              onToggle={toggleSeries} onInterval={setSeriesInterval} onStop={stopSeries} />
          ))}
        </section>
      )}

      {tab === "csv" && (
        <section className="max-w-2xl bg-white border border-navy/10 p-6" data-testid="team-sevdesk-csv-section">
          <div className="font-display text-lg text-navy">CSV-Import: E-Mail ↔ Kundennummer</div>
          <p className="text-[12.5px] text-navy-dark/70 mt-1 leading-relaxed">
            Zwei Spalten: <code>email</code> und <code>sevdesk_customer_number</code> (auch <code>kundennummer</code> erkannt). Erste Zeile Header. Trenner Komma oder Semikolon.
            Nach dem Import werden bereits hochgeladene, nicht zugeordnete Rechnungen automatisch dem Kunden zugewiesen.
          </p>
          <label className="inline-flex items-center gap-2 mt-3 px-4 py-2 text-[11px] tracking-widest uppercase cursor-pointer" style={{ background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory }}>
            <Upload className="w-3.5 h-3.5" /> CSV hochladen
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCsv} data-testid="team-sevdesk-csv-input" />
          </label>
        </section>
      )}

      {tab === "test" && (
        <section className="max-w-2xl bg-white border border-navy/10 p-6 space-y-3" data-testid="team-sevdesk-test-section">
          <div className="font-display text-lg text-navy">Testdaten anlegen</div>
          <p className="text-[12.5px] text-navy-dark/70 leading-relaxed">
            Erzeugt für jeden Testkunden 3 klar markierte Beispielrechnungen (offen · bezahlt · teilbezahlt) plus 2 unzugeordnete, damit Sie den kompletten Flow ohne echte PDFs testen können.
          </p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={seedTest} disabled={busy} data-testid="team-sevdesk-seed-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-widest uppercase disabled:opacity-40"
              style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
              <RefreshCw className="w-3.5 h-3.5" /> Testdaten erzeugen
            </button>
            <button type="button" onClick={deleteTest} disabled={busy || (stats?.test_invoices || 0) === 0} data-testid="team-sevdesk-delete-test-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-widest uppercase disabled:opacity-40 border border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5" /> Alle Testdaten löschen
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
